// updatePusherData.js
'use strict';
import { addBreadcrumb } from '@sentry/browser';

import { MERGE_OPERATION_DATA } from '../ActionTypes.js';
import getOperationData from '../selector/getOperationData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import { pusher as pusherDebug } from '../resource/debug.js';
import { Severity } from '../resource/sentry.js';

const pusherDebugLog = pusherDebug.extend('log:updatePusherData');
const pusherDebugError = pusherDebug.extend('error:updatePusherData');

const pusherLog = ({ level = Severity.Info, messages, data }) => {
  addBreadcrumb({
    category: 'pusher',
    level,
    message: messages.join(' '),
    data,
  });
  const logger = level === Severity.Error ? pusherDebugError : pusherDebugLog;
  if (data) {
    return logger(...messages, data);
  }
  return logger(...messages);
};

/**
 * update Pusher Connection data
 * @kind action
 * @param {object} params
 * @param {object} params.data connection data to be merged
 * @return {Promise} Action promise.
 */
export const updatePusherConnectionData =
  ({ data = {} } = {}) =>
  async (dispatch, getState) => {
    const timestampOffsetMsecs = getTimestampOffset(
      getState(),
      Accuracy.MILLISECOND
    );
    const timestamp = Date.now() + timestampOffsetMsecs;
    data.timestamp = timestamp;

    const currentState = data.current || data.state;
    const previousState =
      data.previous ||
      getOperationData(getState(), ['pusher', 'connection'], 'state');
    const level = ['unavailable', 'failed', 'disconnected'].includes(
      data?.state
    )
      ? Severity.Error
      : Severity.Info;
    data.previousState = previousState;
    pusherLog({
      level,
      messages: ['updatePusherConnectionData()', 'state:', currentState],
      data,
    });

    const stats = {};
    const previousTimestamp = getOperationData(
      getState(),
      ['pusher', 'connection'],
      'timestamp'
    );
    if ('initializing' === previousState && 'connected' === currentState) {
      stats.initSpeed = `${timestamp - previousTimestamp}ms`;
      stats.connectSpeed = '(reused)';
    } else if (
      'initializing' === previousState &&
      'connecting' === currentState
    ) {
      stats.initSpeed = `${timestamp - previousTimestamp}ms`;
    } else if ('connecting' === previousState && 'connected' === currentState) {
      stats.connectSpeed = `${timestamp - previousTimestamp}ms`;
    }

    return dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['pusher'],
        data: {
          connection: data,
          stats,
        },
      },
    });
  };

/**
 * update Pusher Connection data
 * @kind action
 * @param {object} params
 * @param {object} params.data connection data to be merged
 * @return {Promise} Action promise.
 */
export const updatePusherChannelsData =
  ({ data }) =>
  async dispatch => {
    pusherLog({
      messages: ['updatePusherChannelsData()'],
      data,
    });
    return dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['pusher', 'channels'],
        data,
      },
    });
  };
